import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { FormElement, MobileListItem } from '@mobileTypes'
import { useMobileListItems } from '@mobileHooks'
import { useFetchAllDisplayRulesQuery, useFetchDisplayRuleByIdQuery } from '@redux/api/displayRules.api'
import { DisplayRule } from '@types'
import { SingleListItemRenderer } from './SingleListItemRenderer'

interface ListItemElementPreviewProps {
  element: FormElement
  itemData?: any // Data to display (from variable or navigation params)
}

type DisplayRuleLike = {
  id?: number | string | null
  internalId?: string | null
  [key: string]: any
}

const addDisplayRuleRef = (refs: Map<string, DisplayRuleLike>, displayRule?: DisplayRuleLike | null) => {
  if (!displayRule) {
    return
  }

  if (displayRule.internalId) {
    refs.set(displayRule.internalId, displayRule)
  }
}

const collectDisplayRuleRefs = (listItem?: Partial<MobileListItem> | null) => {
  const refs = new Map<string, DisplayRuleLike>()

  addDisplayRuleRef(refs, listItem?.icon?.displayRule)
  listItem?.rows?.forEach(row => {
    row.elements?.forEach(element => addDisplayRuleRef(refs, element.displayRule))
  })

  const traverseTemplate = (children: any[] = []) => {
    children.forEach(child => {
      addDisplayRuleRef(refs, child.displayRule)
      if (child.type === 'view') {
        traverseTemplate(child.children || [])
      }
    })
  }
  traverseTemplate(listItem?.itemTemplate?.children || [])

  return Array.from(refs.values())
}

export const ListItemElementPreview: FC<ListItemElementPreviewProps> = ({
  element,
  itemData,
}) => {
  const { getListItemById } = useMobileListItems()

  // Get list item configuration
  const listItemConfig = useMemo(() => {
    if (element.listItemConfigId) {
      return getListItemById(element.listItemConfigId)
    }
    return null
  }, [element.listItemConfigId, getListItemById])

  const { data: allDisplayRules = [] } = useFetchAllDisplayRulesQuery({})
  const displayRuleRefs = useMemo(() => collectDisplayRuleRefs(listItemConfig), [listItemConfig])
  const displayRuleIds = useMemo(() => {
    const ids = new Set<number>()

    displayRuleRefs.forEach(displayRule => {
      const matchedDisplayRule = allDisplayRules.find(
        rule => rule.internalId === displayRule.internalId
      )

      if (matchedDisplayRule?.id) {
        ids.add(Number(matchedDisplayRule.id))
      }
    })

    return Array.from(ids)
  }, [allDisplayRules, displayRuleRefs])

  const query1 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[0] }, { skip: !displayRuleIds[0] })
  const query2 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[1] }, { skip: !displayRuleIds[1] })
  const query3 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[2] }, { skip: !displayRuleIds[2] })
  const query4 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[3] }, { skip: !displayRuleIds[3] })
  const query5 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[4] }, { skip: !displayRuleIds[4] })
  const query6 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[5] }, { skip: !displayRuleIds[5] })
  const query7 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[6] }, { skip: !displayRuleIds[6] })
  const query8 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[7] }, { skip: !displayRuleIds[7] })
  const query9 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[8] }, { skip: !displayRuleIds[8] })
  const query10 = useFetchDisplayRuleByIdQuery({ id: displayRuleIds[9] }, { skip: !displayRuleIds[9] })
  const displayRulesMap = useMemo(() => {
    const map = new Map<number | string, DisplayRule>()
    const queries = [query1, query2, query3, query4, query5, query6, query7, query8, query9, query10]

    queries.forEach(query => {
      if (!query.data) {
        return
      }

      const displayRule = query.data
      if (displayRule.internalId) {
        map.set(displayRule.internalId, displayRule)
      }
    })

    return map
  }, [query1, query2, query3, query4, query5, query6, query7, query8, query9, query10])

  // Mock data for preview if no itemData provided
  const displayData = useMemo(() => {
    if (itemData) {
      return itemData
    }
    // Return mock data for preview
    return {
      _id: 'preview_1',
      title: 'Sample Item',
      description: 'This is a preview of the list item',
      status: 'active',
    }
  }, [itemData])

  if (!listItemConfig) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No list item configuration selected
        </Typography>
      </Box>
    )
  }

  // Create a complete MobileListItem for the preview
  const previewListItem: MobileListItem = {
    ...listItemConfig,
    id: listItemConfig.id || 'preview',
    title: listItemConfig.title || 'Preview',
    itemType: listItemConfig.itemType || 'dictionary',
  }

  return (
    <SingleListItemRenderer
      item={previewListItem}
      record={displayData}
      displayRulesMap={displayRulesMap}
      showChevron={false}
    />
  )
}
